import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useKunden } from "../hooks/usePlanung";
import { useKundenLage } from "../hooks/useKundenLage";
import type { Kunde } from "../lib/types";
import KundeAnsicht from "../components/kunden/KundeAnsicht";
import KundeNeuDialog from "../components/kunden/KundeNeuDialog";
import KundenListe from "../components/kunden/KundenListe";
import { headRow, successBanner, bannerClose } from "../components/kunden/kundenStile";
import "../styles/kunden.css";
import "../styles/kundenfluss.css";

// Liste und Ansicht im Entwurf B (03.11.2026): KundenListe links, KundeAnsicht rechts;
// am Telefon das eine oder das andere (kunden.css, .kd-split.ist-gewaehlt).
type Ui = { mode: "none" } | { mode: "create" } | { mode: "edit"; id: string };

// ── Seite ─────────────────────────────────────────────────────────────────
export default function KundenPage() {
  const { data: kunden = [], isLoading, isError } = useKunden();
  const { nachId: lage } = useKundenLage();
  // Der gewaehlte Kunde steht in der Adresse (?kunde=…), nicht nur im State.
  // Nur so ist der Weg „Kunde -> Projekt -> zurueck" verlustfrei: der
  // Browser-Zurueck landet auf genau diesem Eintrag der History und damit
  // wieder auf demselben Kunden.
  const [suchParams, setSuchParams] = useSearchParams();
  const kundeParam = suchParams.get("kunde") || "";
  const [ui, setUi] = useState<Ui>({ mode: "none" });
  const [banner, setBanner] = useState<null | string>(null);

  const editId = ui.mode === "edit" ? ui.id : "";

  // Beim Ankommen (auch per Zurueck aus einem Projekt) den Kunden aus der
  // Adresse oeffnen und in den Blick scrollen. Der Ref merkt, welche ID zuletzt
  // hergestellt wurde, damit ein Klick in der Liste nicht sofort wieder von
  // diesem Effekt ueberschrieben wird.
  const hergestellt = useRef("");
  useEffect(() => {
    if (!kundeParam || hergestellt.current === kundeParam) return;
    const treffer = kunden.find((k) => k.id === kundeParam);
    if (!treffer) return;
    hergestellt.current = kundeParam;
    openRow(treffer, true);
    // Die Zeile kann ausserhalb des sichtbaren Ausschnitts liegen.
    window.requestAnimationFrame(() => {
      document
        .querySelector(`[data-kunde-zeile="${CSS.escape(kundeParam)}"]`)
        ?.scrollIntoView({ block: "center" });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kundeParam, kunden]);

  function openRow(k: Kunde, ausAdresse = false) {
    setBanner(null);
    setUi({ mode: "edit", id: k.id });
    // replace, nicht push: sonst waere jeder Klick in der Liste ein eigener
    // History-Eintrag, und ein einziges Zurueck aus dem Projekt fuehrte nur
    // zum vorletzten angeklickten Kunden.
    if (!ausAdresse && kundeParam !== k.id) setSuchParams({ kunde: k.id }, { replace: true });
    // Auf dem Telefon stehen Liste und Detail untereinander. Ohne diesen Sprung
    // passiert nach dem Antippen scheinbar nichts — das Formular liegt dann
    // ausserhalb des Bildes.
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 760px)").matches) {
      window.requestAnimationFrame(() => {
        document.querySelector(".kunden-detail")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }

  function startNew() {
    setBanner(null);
    setUi({ mode: "create" });
  }

  /* Neu angelegt: gleich auswählen — rechts steht dann die Ansicht zum Weiterpflegen. */
  function angelegt(k: Kunde) {
    setBanner(`„${k.name}“ ist in der Kundenliste.`);
    hergestellt.current = k.id;
    setUi({ mode: "edit", id: k.id });
    setSuchParams({ kunde: k.id }, { replace: true });
  }

  function closePanel() {
    setUi({ mode: "none" });
    if (kundeParam) setSuchParams({}, { replace: true });
  }


  return (
    <div className="kunden-seite">
      <div style={headRow}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800 }}>Kunden</h1>
        <div style={{ color: "var(--vg-text-3)", fontSize: 13 }}>{kunden.length}</div>
      </div>

      {banner && (
        <div style={successBanner}>
          <span>{banner}</span>
          <button onClick={() => setBanner(null)} style={bannerClose}>Schließen</button>
        </div>
      )}

      <div className={"kunden-split kd-split" + (editId ? " ist-gewaehlt" : "")}>
        <div className="kunden-liste ku-listen-spalte">
          <KundenListe kunden={kunden} lage={lage} laedt={isLoading} fehler={isError} gewaehlt={editId} onWaehlen={(k) => openRow(k)} onNeu={startNew} />
        </div>

        {/* ── Rechte Spalte: Detail / Formular ──────────────────── */}
        <div className="kunden-detail kd-spalte">
          {ui.mode === "edit" && kunden.some((k) => k.id === ui.id) ? (
            <KundeAnsicht key={editId} kunde={kunden.find((k) => k.id === ui.id) as Kunde} onSchliessen={closePanel} />
          ) : (
            <div className="lf-leerblatt">
              <h3>Kein Kunde ausgewählt</h3>
              <p>
                Wähle links einen Kunden. Rechts siehst du dann, was bei ihm ansteht, seine Projekte, Daten und den
                Verlauf. Geändert wird über „Bearbeiten“. Neue Kunden legst du mit ＋ neben der Suche an.
              </p>
            </div>
          )}
        </div>
      </div>
      {ui.mode === "create" && (
        <KundeNeuDialog
          hinweis="landet in der Kundenliste"
          onAngelegt={angelegt}
          onSchliessen={() => setUi({ mode: "none" })}
        />
      )}
    </div>
  );
}
